"use client";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import RequestInfo from "@packages/shared-components/common-utilities/cards/interaction-button/requestinfo";
import Getprospectus from "@packages/shared-components/common-utilities/cards/interaction-button/getprospectus";
import Visitwebsite from "@packages/shared-components/common-utilities/cards/interaction-button/visitwebsite";
import BookOpenDay from "@packages/shared-components/common-utilities/cards/interaction-button/bookopenday";
import UserFavourite from "@packages/shared-components/common-utilities/user-favourite/UserFavourite";
import Link from "next/link";
import ApplyNow from "@packages/shared-components/common-utilities/cards/interaction-button/applynow";
import {
  GA4DataLayer,
  GA4DataLayerFn,
  providerTypeWithSubOrder,
} from "@packages/lib/utlils/helper-function";
import {
  DataLayerGA4AttrType,
  GA4EventAttrType,
} from "@packages/lib/types/datalayerGA";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import { GAConstants } from "@packages/shared-components/services/constants";
import { getCustomDomain } from "@packages/lib/utlils/common-function-server";
interface ProviderResultsCardProps {
  searchResultlist: any[]; // Adjust type as needed
  gaStats?: DataLayerGA4AttrType;
  csCommonData?: ClickstreamInterface;
  pageNo?: any;
  hcCollegeId?: any;
  commonOldGAAttrs?: DataLayerGA4AttrType;
}

const ProviderResultsCard: React.FC<ProviderResultsCardProps> = ({
  searchResultlist = [],
  gaStats,
  csCommonData,
  pageNo,
  hcCollegeId,
  commonOldGAAttrs,
}) => {
  const searchParams = useSearchParams();
  const selectedSubject =
    searchParams?.get("subject")?.split(" ")[0] ??
    searchParams?.get("subject") ??
    searchParams?.get("q");
  const collegeNameKeyText = searchParams?.has("university")
    ? searchParams?.get("university")
    : "";
  // State to track which cards' modules are visible
  const [visibleModules, setVisibleModules] = useState<boolean[]>(
    new Array(searchResultlist.length).fill(true), // Initially, all are closed
  );

  // Toggle function for a specific card's modules
  const toggleModuleVisibility = (index: number) => {
    setVisibleModules(
      (prev) => prev.map((state, i) => (i === index ? !state : state)), // Toggle only the clicked card
    );
  };

  const handleGAlog = (data: any) => {
    GA4DataLayer({
      ...gaStats,
      event: GAConstants.track_event,
      event_name: "course_click",
      course_name: data?.title,
    });
  };
  const favouritesData = (data?: any, index?: number): GA4EventAttrType => {
    const position = typeof index === "number" ? index + 1 : 0;

    return {
      institution_id: String(data?.collegeId ?? ""),
      institution_name: collegeNameKeyText ?? "",
      provider_type: providerTypeWithSubOrder(data?.subOrderItemId) ?? "",
      course_name: data?.title ?? "",
      study_mode: data?.tagLocation ?? "",
      position: position,
    };
  };

  const getEnquiryProps = (items: any, index: any) => {
    return {
      courseId: items?.courseId,
      collegeId: hcCollegeId,
      subOrderItemId: items?.subOrderItemId,
      sponsoredListingFlag: items?.sponsoredListingFlag,
      manualBoostingFlag: items?.manualBoostingFlag,
      orderItemId: items?.orderItemId,
      collegeName: collegeNameKeyText,
      pageName: items?.pageName,
      selectedSubject: selectedSubject,
      qualCode: items?.qualCode,
      courseName: items?.title,
      displayIndex: Number(index) + 1,
      csPageName: "Provider Result",
      studyMode: items?.tagLocation,
      browseNodeSubjects: csCommonData?.browseNodeSubjects,
      countryOfResidence: csCommonData?.countryOfResidence,
      paginationNumber: pageNo,
    };
  };

  useEffect(() => {
    if (searchResultlist?.length > 0) {
      emitter.emit("providerresultloading", false);
      const prPageBodySection =
        window.document.getElementById("prPageBodySection");
      prPageBodySection?.classList.remove("hidden");
    }
  }, [searchResultlist]);
  return (
    <>
      {searchResultlist?.map((items, index) => {
        const enquiryDetails = getEnquiryProps(items, index);
        return (
          <div
            key={index}
            // className="flex flex-col rounded-[16px] overflow-hidden bg-white shadow-custom-3 border border-grey-200 w-full md:max-w-[calc(50%_-_10px)] xl:max-w-[385px]"
            className="flex flex-col rounded-[16px] overflow-hidden bg-white shadow-custom-3 border border-grey-200"
          >
            <div
              className={`flex justify-end p-[16px] ${process.env.SITE_CODE === "PGS_WEB" ? "bg-positive-light" : "bg-blue-100"}`}
            >
              {/* <span className="favorite group items-center justify-center flex min-w-[40px] w-[40px] h-[40px]  border border-primary-400 hover:bg-primary-400 rounded-[48px] cursor-pointer"> */}
              <UserFavourite
                contentId={items?.courseId}
                contentName={items?.title}
                contentType="COURSE"
                statsGAData={favouritesData(items, index)}
                clickStreamData={{
                  ...csCommonData,
                  ...getEnquiryProps(items, index),
                }}
                statsOldGAData={{
                  college_id: String(items?.collegeId ?? ""),
                  college_name: collegeNameKeyText ?? "",
                  provider_type:
                    providerTypeWithSubOrder(items?.subOrderItemId) ?? "",
                  course_name: items?.title ?? "",
                  study_mode: items?.tagLocation ?? "",
                  ...commonOldGAAttrs,
                }}
              />
              {/* </span> */}
            </div>
            <div className="flex p-[16px] flex-col gap-[16px] h-full justify-between">
              <div className="flex flex-col gap-[16px] md:min-h-[240px]">
                <Link
                  prefetch={false}
                  href={items?.cdpagesurl}
                  onClick={() => handleGAlog(items)}
                  className="h6 hover:underline cursor-pointer text-blue-400"
                >
                  {items?.title}
                </Link>
                <div className="flex flex-wrap gap-[4px]">
                  {items?.points && (
                    <li className="flex gap-[2px] bg-grey-100 text-grey-500 uppercase font-semibold xs-small px-[8px] rounded-[4px]">
                      <Image
                        alt="calender icon"
                        width="16"
                        height="16"
                        src="../static/assets/icons/search-result/calender-grey.svg"
                      />
                      {items?.points} UCAS POINTS
                    </li>
                  )}
                  {items?.tagLocation && (
                    <li className="flex gap-[2px] bg-grey-100 text-grey-500 font-semibold uppercase xs-small px-[8px] rounded-[4px]">
                      <Image
                        alt="timer icon"
                        width="16"
                        height="16"
                        src="../static/assets/icons/search-result/time-grey.svg"
                      />
                      {items.tagLocation}
                    </li>
                  )}
                </div>

                {items?.fullCourse && (
                  <span className="text-blue-400 font-semibold small">
                    {items.fullCourse}
                  </span>
                )}

                {process.env.SITE_CODE === "PGS_WEB" ? (
                  <>
                    {items?.courseSummary &&
                      items?.courseSummary?.trim() !== "" && (
                        <div className="relative">
                          <div className="rtf-sr text-grey500 line-clamp-3 small">
                            {items?.courseSummary}
                          </div>
                          <div className="absolute bg-gradient13 bg-white bottom-0 right-0 sm:left-[210px]">
                            <span>... </span>
                            <Link
                              onClick={() => handleGAlog(items)}
                              href={items?.cdpagesurl}
                              className="text-blue-400 cursor-pointer small hover:underline"
                            >
                              Read More
                            </Link>
                          </div>
                        </div>
                      )}
                  </>
                ) : (
                  <>
                    {items?.modulesList && (
                      <>
                        <button
                          onClick={() => toggleModuleVisibility(index)}
                          className={`text-left text-blue-400 select-none font-semibold small cursor-pointer transition-all delay-0 duration-300 ease-linear pl-[20px] relative before:absolute before:content-[''] before:w-[11px] before:h-[2px] before:bg-blue-400 before:rounded-[2px] before:left-[2px] before:top-[10px] after:absolute after:content-[''] after:w-[11px] after:h-[2px] after:bg-blue-400 after:rounded-[2px] after:left-[2px] after:top-[10px] ${visibleModules[index] ? "" : "after:rotate-90"} after:transition-all after:delay-0 after:duration-300 after:ease-linear`}
                        >
                          Modules
                        </button>
                        <ul className="p-[0_16px_0_18px] flex flex-col gap-[8px] list-disc">
                          {visibleModules[index] &&
                            items?.modulesList
                              ?.split("###")
                              ?.map((desc: any, listIndex: number) => (
                                <li
                                  className="text-grey300 small break-word"
                                  key={listIndex}
                                >
                                  {desc}
                                </li>
                              ))}
                        </ul>
                      </>
                    )}

                    {/* "See all modules" Link */}
                    {visibleModules[index] && items?.modulesList?.length && (
                      <span className="text-blue-400 hover:underline select-none font-semibold small cursor-pointer">
                        <Link
                          prefetch={false}
                          href={items?.cdpagesurl}
                          onClick={() =>
                            GA4DataLayer({
                              event: GAConstants.track_event,
                              event_name: "redirection_link_click",
                              course_name: items.title,
                              click_text: "See all modules",
                              field: getCustomDomain() + items?.cdpagesurl,
                              ...gaStats,
                            })
                          }
                        >
                          See all modules
                        </Link>
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="w-full flex flex-col gap-[8px]">
                {process.env.PROJECT === "PGS" && items?.hasApplyNow && (
                  <ApplyNow
                    enquiryProps={enquiryDetails}
                    commonGAAttrs={gaStats}
                    commonOldGAAttrs={commonOldGAAttrs}
                  />
                )}
                {items?.hasProspectus && (
                  <Getprospectus
                    enquiryProps={enquiryDetails}
                    commonGAAttrs={gaStats}
                    commonOldGAAttrs={commonOldGAAttrs}
                  />
                )}
                {items?.hasWebsite && (
                  <Visitwebsite
                    enquiryProps={enquiryDetails}
                    commonGAAttrs={gaStats}
                    commonOldGAAttrs={commonOldGAAttrs}
                  />
                )}
                {items?.hasOpendayFlag && (
                  <BookOpenDay
                    enquiryProps={enquiryDetails}
                    commonGAAttrs={gaStats}
                    commonOldGAAttrs={commonOldGAAttrs}
                  />
                )}
                {((items?.hasEmail && items?.subOrderItemId) ||
                  (!items?.hasEmail &&
                    !items?.subOrderItemId &&
                    items?.sponsoredListingFlag !== "Y")) && (
                  <RequestInfo
                    enquiryProps={enquiryDetails}
                    commonGAAttrs={gaStats}
                    commonOldGAAttrs={commonOldGAAttrs}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ProviderResultsCard;
